import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faClock,
  faPaperPlane,
  faUser,
  faComment,
  faCheckCircle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import './Contact.css';
import heroBg from '../images/home_contact_img_1920x780.webp';

const Contact = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [formStatus, setFormStatus] = useState({ submitted: false, loading: false, error: null });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, submitted: false, error: null });
    
    setTimeout(() => {
      setFormStatus({ loading: false, submitted: true, error: null });
    }, 1500);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  const contactInfos = [
    { icon: faMapMarkerAlt, title: t('titre_adresse'), value: t('adresse'), color: '#0066cc' },
    { icon: faPhone, title: 'Téléphone', value: '+237 681 134 804 / +237 696 801 921', color: '#90ee90' },
    { icon: faEnvelope, title: 'Email', value: 'alltechnologiesincameroon@gmail.com', color: '#ffb74d' },
    { icon: faClock, title: t('titre_horaire'), value: t('horaire_work_contact'), color: '#f06292' },
  ];

  return (
    <div className="contact-page">
      {/* ===== HERO ===== */}
      <section 
        className="contact-hero" 
        style={{ 
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="contact-hero-overlay"></div>
        <div className="hero-particles">
          <span></span><span></span><span></span><span></span>
          <span></span><span></span><span></span><span></span>
        </div>
        <div className="container">
          <motion.div
            className="contact-hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="badge-dot"></span>
              {t('btn_Nous_Contacter')}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              {t('titre_Contact')}
            </motion.h1>

            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {t('description_Contact')}
            </motion.p>

            <motion.div
              className="contact-breadcrumb"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <span><a href="/">{t('menu_Accueil')}</a></span>
              <span className="separator">›</span>
              <span className="current">{t('btn_Nous_Contacter')}</span>
            </motion.div>

            <div className="hero-line-wrapper">
              <div className="hero-line"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CONTACT SECTION ===== */}
      <section className="contact-section" ref={ref}>
        <div className="container">
          <div className="contact-grid">
            {/* ===== INFOS CONTACT ===== */}
            <motion.div
              className="contact-info-wrapper"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
            >
              <motion.div className="contact-info-header" variants={itemVariants}>
                <h2>{t('send_msg_contact')}</h2>
                <p>{t('description_Contact')}</p>
              </motion.div>

              {contactInfos.map((info, index) => (
                <motion.div
                  key={index}
                  className="contact-info-item"
                  variants={itemVariants}
                  whileHover={{ x: 6 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="info-icon" style={{ backgroundColor: info.color + '15', color: info.color }}>
                    <FontAwesomeIcon icon={info.icon} />
                  </div>
                  <div className="info-content">
                    <h4>{info.title}</h4>
                    <p>{info.value}</p>
                  </div>
                </motion.div>
              ))}

              <motion.div className="contact-social" variants={itemVariants}>
                <h4>{t('follow_us')}</h4>
                <div className="social-links">
                  <a href="https://www.facebook.com/profile.php?id=100080652765508" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href="https://www.instagram.com/atchhub237?igsi=ejB6c2tiZHl3MnAw" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <i className="fab fa-instagram"></i>
                  </a>
                  <a href="https://www.linkedin.com/company/118613966/admin/dashboard/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                  <a href="https://www.tiktok.com/@atc_hub_techpreneuriat?_r=1&_t=ZS-99QD1gHuP3d" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                    <i className="fab fa-tiktok"></i>
                  </a>
                </div>
              </motion.div>
            </motion.div>

            {/* ===== FORMULAIRE ===== */}
            <motion.div
              className="contact-form-wrapper"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
            >
              <motion.div className="contact-form-card" variants={itemVariants}>
                <h3>{t('send_msg_contact')}</h3>
                <p>{t('description_Contact')}</p>

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name">
                      <FontAwesomeIcon icon={faUser} />
                      {t('form_name_label')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      placeholder={t('form_name_placeholder')}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <FontAwesomeIcon icon={faEnvelope} />
                      {t('form_email_label')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      placeholder={t('form_email_placeholder')}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">
                      <FontAwesomeIcon icon={faPaperPlane} />
                      {t('form_subject_label')}
                    </label>
                    <input
                      type="text"
                      id="subject"
                      placeholder={t('form_subject_placeholder')}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">
                      <FontAwesomeIcon icon={faComment} />
                      {t('form_message_label')}
                    </label>
                    <textarea
                      id="message"
                      rows="5"
                      placeholder={t('form_message_placeholder')}
                      required
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={formStatus.loading}
                  >
                    {formStatus.loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="spinner" />
                        {t('form_sending')}
                      </>
                    ) : formStatus.submitted ? (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        {t('btn_submit_form_contact')}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        {t('btn_submit_form_contact')}
                      </>
                    )}
                  </button>

                  {formStatus.submitted && (
                    <div className="form-success">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {t('form_success')}
                    </div>
                  )}

                  {formStatus.error && (
                    <div className="form-error">
                      {t('form_error')}
                    </div>
                  )}
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== MAP ===== */}
      <section className="contact-map">
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3980.545424514147!2d11.495228414332185!3d3.866958949211362!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x108bcf7c7225e545%3A0x8f5f3a7c7a5a5a5a!2sYaound%C3%A9%2C%20Cameroon!5e0!3m2!1sen!2sfr!4v1641234567890!5m2!1sen!2sfr"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="ATC Location"
          ></iframe>
          <div className="map-overlay">
            <div className="map-badge">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>{t('adresse')}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;